import { MyProjects } from '@platform/client';
import { Card, Tabs, TabItem } from '@platform/components';
import { RepUserProjectPanel } from './RepUserProjectPanel';
import EmptyMyProjectsTabContent from './EmptyMyProjectsTab';
import Skeleton from 'react-loading-skeleton';
import { CardStatusEnum, RepMyProjectsResponse, RepProjectPanel } from '@platform/client/schemas';
import { RepCancelledPendingConnectionsAlert, getNonDimissedIds } from './PendingConnectionAlert';
import { useSearch } from 'wouter';
import { DeleteProjectModalProvider } from './Modals/DeleteProjectModal';

const RepUserMyProjects = () => {
  const projectData = MyProjects.useGetOrgUserMyProjects();
  const activeProjects = projectData.data?.active ?? [];
  const inactiveProjects = projectData.data?.inactive ?? [];
  const underReviewProjects = projectData.data?.underReview ?? [];

  // Handle url params for tab or introductionId
  const urlSearchParams = new URLSearchParams(useSearch());
  let selectedTab = urlSearchParams.get('tab');
  const paramIntroId = parseInt(urlSearchParams.get('introductionId') ?? '');
  if (paramIntroId && projectData.data) {
    selectedTab = findTabByIntroId(paramIntroId, projectData.data);
  }

  if (!projectData.isSuccess) {
    return (
      <div className='p-10 flex flex-col md:w-full max-w-[910px] pb-12 md:pb-36 gap-6'>
        {[1, 2, 3].map((_, index) => (
          <div key={index}>
            <Card
              headerContent='&nbsp;'
              headerColor='gray-50'
              buttonText=''
              buttonDisabled={true}
              content={<Skeleton className='w-48' />}
              contentSubText='&nbsp;'
              avatarSize='md'
              badges={[]}
              variant='small'
              buttonOnClick={() => {}}
            />
          </div>
        ))}
      </div>
    );
  }

  // Manage closed pending intros alert if recently matched
  const recentMatchesWithSysDeclines = getRecentMatchesWithSysDeclines(activeProjects);
  const nonDismissedProjectIds = getNonDimissedIds(recentMatchesWithSysDeclines);

  const tabPanels: TabItem[] = [
    createTabPanels('Active projects', activeProjects),
    createTabPanels('Inactive projects', inactiveProjects),
  ];

  if (underReviewProjects.length > 0) {
    tabPanels.push(createTabPanels('Under review', underReviewProjects));
  }

  return (
    <DeleteProjectModalProvider>
      <div>
        {/* div to preserve 48px gap regardless of alert */}
        <div className='min-h-[48px] flex justify-center'>
          {nonDismissedProjectIds.length > 0 && (
            <RepCancelledPendingConnectionsAlert projectIds={nonDismissedProjectIds} />
          )}
        </div>
        <div>{selectedTab}</div>
        <div>
          <Tabs
            title='Rep User My Projects Tabs'
            items={tabPanels}
            defaultSelectedKey={selectedTab ?? undefined}
          />
        </div>
      </div>
    </DeleteProjectModalProvider>
  );
};

const createTabPanels = (key: string, panelData: RepProjectPanel[]): TabItem => {
  return {
    id: key,
    content:
      panelData.length > 0 ?
        <div className='mt-12'>
          {panelData.map((panel, i) => (
            <div key={`${key}-${i}`} className='my-6'>
              <RepUserProjectPanel {...panel} />
            </div>
          ))}
        </div>
      : <EmptyMyProjectsTabContent tabKey={key} userType='rep' />,
  };
};

const getRecentMatchesWithSysDeclines = (activeProjects: RepProjectPanel[]) => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const activeRecentProjects = activeProjects.filter((projectPanel) => {
    const assignmentCards = projectPanel.actionRequired.filter((card) => {
      return (
        card.cardStatus === CardStatusEnum.project_active &&
        new Date(card.cardStatusChangedAt) > thirtyDaysAgo
      );
    });
    const teamAssignmentCards = projectPanel.teamCards.filter((card) => {
      return (
        card.cardStatus === CardStatusEnum.project_active &&
        new Date(card.cardStatusChangedAt) > thirtyDaysAgo
      );
    });
    return assignmentCards.length > 0 || teamAssignmentCards.length > 0;
  });
  return activeRecentProjects
    .filter((projectPanel) => {
      const sysDeclinedCards = projectPanel.inactive.filter(
        (card) =>
          card.cardStatus === CardStatusEnum.intro_system_declined_other_pro ||
          card.cardStatus === CardStatusEnum.discovery_system_declined_other_pro,
      );
      return sysDeclinedCards.length > 0;
    })
    .map((projectPanel) => projectPanel.project?.id as number);
};

const findTabByIntroId = (introId: number, projectPanels: RepMyProjectsResponse) => {
  const introIdToTabMap: Record<number, string> = {};
  projectPanels.active.forEach((panel) => {
    const allActiveCards = [...panel.actionRequired, ...panel.inProgress, ...panel.inactive];
    allActiveCards.forEach((card) => {
      introIdToTabMap[card.introduction.id] = 'Active projects';
    });
  });
  projectPanels.inactive.forEach((panel) => {
    const allInactiveCards = [...panel.actionRequired, ...panel.inProgress, ...panel.inactive];
    allInactiveCards.forEach((card) => {
      introIdToTabMap[card.introduction.id] = 'Inactive projects';
    });
  });
  return introIdToTabMap[introId];
};

export default RepUserMyProjects;
